import { createAsyncThunk } from '@reduxjs/toolkit';

import { fetchWrapper } from '../../../../../helpers';
import { INTAKE_QUESTIONNAIRES_API_BASE } from '../../constants';

import type {
  CreateIntakeMedicalDiagnosisPayload,
  CreateIntakeMedicalSelectionPayload,
  DeleteIntakeMedicalDiagnosisPayload,
  DeleteIntakeMedicalSelectionPayload,
  IIntakeMedicalDiagnosis,
  IIntakeMedicalInfo,
  IIntakeMedicalSelection,
  UpdateIntakeMedicalDiagnosisPayload,
  UpdateIntakeMedicalInfoPayload,
} from './types';

export function createIntakeMedicalThunks() {
  return {
    updateIntakeMedicalInfo: updateIntakeMedicalInfo(),
    createIntakeMedicalDiagnosis: createIntakeMedicalDiagnosis(),
    updateIntakeMedicalDiagnosis: updateIntakeMedicalDiagnosis(),
    deleteIntakeMedicalDiagnosis: deleteIntakeMedicalDiagnosis(),
    createIntakeMedicalSelection: createIntakeMedicalSelection(),
    deleteIntakeMedicalSelection: deleteIntakeMedicalSelection(),
  };
}

function updateIntakeMedicalInfo() {
  return createAsyncThunk<IIntakeMedicalInfo, UpdateIntakeMedicalInfoPayload>(
    'intakeQuestionnaire/updateIntakeMedicalInfo',
    async ({ intakeQuestionnaireId, data, getAccessTokenSilently }) =>
      fetchWrapper.patch(
        {
          url: `${INTAKE_QUESTIONNAIRES_API_BASE}/${intakeQuestionnaireId}/medical_info`,
          getAccessTokenSilently,
        },
        data,
      ),
  );
}

function createIntakeMedicalDiagnosis() {
  return createAsyncThunk<IIntakeMedicalDiagnosis, CreateIntakeMedicalDiagnosisPayload>(
    'intakeQuestionnaire/createIntakeMedicalDiagnosis',
    async ({ intakeQuestionnaireId, data, getAccessTokenSilently }) =>
      fetchWrapper.post(
        {
          url: `${INTAKE_QUESTIONNAIRES_API_BASE}/${intakeQuestionnaireId}/medical_info/diagnosis`,
          getAccessTokenSilently,
        },
        data,
      ),
  );
}

function updateIntakeMedicalDiagnosis() {
  return createAsyncThunk<IIntakeMedicalDiagnosis, UpdateIntakeMedicalDiagnosisPayload>(
    'intakeQuestionnaire/updateIntakeMedicalDiagnosis',
    async ({ intakeQuestionnaireId, diagnosisId, data, getAccessTokenSilently }) =>
      fetchWrapper.patch(
        {
          url: `${INTAKE_QUESTIONNAIRES_API_BASE}/${intakeQuestionnaireId}/medical_info/diagnosis/${diagnosisId}`,
          getAccessTokenSilently,
        },
        data,
      ),
  );
}

function deleteIntakeMedicalDiagnosis() {
  return createAsyncThunk<void, DeleteIntakeMedicalDiagnosisPayload>(
    'intakeQuestionnaire/deleteIntakeMedicalDiagnosis',
    async ({ intakeQuestionnaireId, diagnosisId, getAccessTokenSilently }) => {
      await fetchWrapper.delete({
        url: `${INTAKE_QUESTIONNAIRES_API_BASE}/${intakeQuestionnaireId}/medical_info/diagnosis/${diagnosisId}`,
        getAccessTokenSilently,
      });
    },
  );
}

function createIntakeMedicalSelection() {
  return createAsyncThunk<IIntakeMedicalSelection, CreateIntakeMedicalSelectionPayload>(
    'intakeQuestionnaire/createIntakeMedicalSelection',
    async ({ intakeQuestionnaireId, data, getAccessTokenSilently }) =>
      fetchWrapper.post(
        {
          url: `${INTAKE_QUESTIONNAIRES_API_BASE}/${intakeQuestionnaireId}/medical_info/selection`,
          getAccessTokenSilently,
        },
        data,
      ),
  );
}

function deleteIntakeMedicalSelection() {
  return createAsyncThunk<void, DeleteIntakeMedicalSelectionPayload>(
    'intakeQuestionnaire/deleteIntakeMedicalSelection',
    async ({ intakeQuestionnaireId, selectionType, selectionId, getAccessTokenSilently }) => {
      await fetchWrapper.delete({
        url: `${INTAKE_QUESTIONNAIRES_API_BASE}/${intakeQuestionnaireId}/medical_info/selection/${selectionType}/${selectionId}`,
        getAccessTokenSilently,
      });
    },
  );
}
